import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Capacitor } from '@capacitor/core';
import { FirebaseMessaging } from '@capacitor-firebase/messaging';
import { ApiService } from './api.service';
import { StorageService } from './storage.service';
import { CacheKeys } from '../structure/enums/cache-keys.class';

/**
 * Push notifications, and the phone's registration for them.
 *
 * Two rules shape this. The permission is asked when the member turns a
 * notification on, never at first launch: on iOS a refusal cannot be asked
 * again, and a prompt before anyone has seen the app is a refusal waiting to
 * happen. And what a notification does when tapped is decided by the message,
 * not by this build - a `route` in the payload is followed as given - so the
 * server can send people to something that did not exist when the app shipped.
 *
 * Nothing here runs on the web: there the cookie dialog and its analytics are
 * the whole story, and push belongs to the phone.
 */
@Injectable({ providedIn: 'root' })
export class PushService {
    private listening = false;

    constructor(
        private api: ApiService,
        private storage: StorageService,
        private router: Router,
    ) {}

    /** Push exists on the phones, not in a browser tab. */
    get supported(): boolean {
        return Capacitor.isNativePlatform();
    }

    async permissionGranted(): Promise<boolean> {
        if (!this.supported) {
            return false;
        }

        try {
            const { receive } = await FirebaseMessaging.checkPermissions();
            return receive === 'granted';
        } catch {
            return false;
        }
    }

    /**
     * Ask, and if granted, register this phone. Returns what the member chose,
     * so the screen can put a toggle back if they said no.
     */
    async enable(): Promise<boolean> {
        if (!this.supported) {
            return false;
        }

        try {
            let granted = await this.permissionGranted();

            if (!granted) {
                const { receive } = await FirebaseMessaging.requestPermissions();
                granted = receive === 'granted';
            }

            if (!granted) {
                return false;
            }

            await this.register();
            this.listen();

            return true;
        } catch {
            return false;
        }
    }

    /**
     * Tell the server about this phone. Safe to call again: the server keys on
     * the token, so a repeat is an update, not a duplicate.
     */
    async register(): Promise<void> {
        if (!this.supported) {
            return;
        }

        const { token } = await FirebaseMessaging.getToken();

        if (!token) {
            return;
        }

        await this.storage.set(CacheKeys.FCM_CODE, token);
        await this.api.doRequest('my-account/device', 'POST', {
            fcm_token: token,
            device_name: Capacitor.getPlatform(),
        });
    }

    /**
     * On app start and after login: if the member has already said yes, make
     * sure the server still knows this phone. Tokens are replaced by the phone
     * without anyone signing in again, which is how a device goes quiet.
     */
    async refreshRegistration(): Promise<void> {
        if (!this.supported || !(await this.permissionGranted())) {
            return;
        }

        try {
            await this.register();
            this.listen();
        } catch {
            // A phone we cannot register is not a reason to fail the launch.
        }
    }

    /** Signing out: the server forgets the phone and its topic subscriptions. */
    async forget(): Promise<void> {
        const token = await this.storage.get(CacheKeys.FCM_CODE);

        if (!token) {
            return;
        }

        try {
            await this.api.doRequest('my-account/device', 'DELETE', { fcm_token: token });
        } finally {
            await this.storage.remove(CacheKeys.FCM_CODE);
        }
    }

    /** Attached once: a second set of listeners would act on every message twice. */
    listen(): void {
        if (!this.supported || this.listening) {
            return;
        }

        this.listening = true;

        FirebaseMessaging.addListener('tokenReceived', async (event: any) => {
            if (event?.token) {
                await this.storage.set(CacheKeys.FCM_CODE, event.token);
                await this.api.doRequest('my-account/device', 'POST', {
                    fcm_token: event.token,
                    device_name: Capacitor.getPlatform(),
                }).catch(() => {});
            }
        });

        FirebaseMessaging.addListener('notificationActionPerformed', async (event: any) => {
            await this.open(event?.notification?.data ?? {});
        });
    }

    /**
     * Where a tapped notification goes. The message says: `route` is followed
     * as given, and the older shapes are kept so a notification sent by the
     * current server still lands somewhere sensible.
     */
    private async open(data: any): Promise<void> {
        const route = typeof data?.route === 'string' ? data.route : null;

        if (route) {
            await this.router.navigateByUrl(route).catch(() => {});
            return;
        }

        if (data?.type === 'live_incident' && data?.live_incident_id) {
            await this.router.navigate(['/live-incident', data.live_incident_id]);
            return;
        }

        if (data?.type === 'announcement') {
            await this.router.navigate(['/notifications']);
        }
    }
}
